import React, { useState } from 'react';
import styled from 'styled-components';
import Card from 'components/Card';
import CardHeader from 'components/CardHeader';
import Icon from 'components/Icon';
import { usePerformance } from '../context';
import {
  ActivityType,
  ActivityOutcome,
  CrmStatus,
  BottleDetail,
  WineListSnapshot
} from '../types';
import history from '../../../history';

// ─── Styled ───

const DetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: ${(props) => props.theme.fonts.sfcompact_text};
  font-size: 14px;
  color: #e25d34;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const ProfileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  flex-wrap: wrap;

  .restaurant-info {
    flex: 1;

    .restaurant-name {
      font-family: ${(props) => props.theme.fonts.oswald};
      font-size: 28px;
      color: #2b3d54;
      text-transform: uppercase;
      margin: 0;
    }

    .restaurant-meta {
      display: flex;
      gap: 16px;
      margin-top: 8px;
      font-family: ${(props) => props.theme.fonts.sfcompact_text};
      font-size: 13px;
      color: #6c757d;
      flex-wrap: wrap;
    }

    .meta-item {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }

  .status-controls {
    display: flex;
    gap: 8px;
    align-items: center;
  }
`;

const StatusSelect = styled.select`
  padding: 6px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  font-size: 13px;
  font-family: ${(props) => props.theme.fonts.sfcompact_text};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #e25d34;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

const InfoSection = styled.div`
  .section-title {
    font-family: ${(props) => props.theme.fonts.oswald};
    font-size: 14px;
    font-weight: 600;
    color: #2b3d54;
    text-transform: uppercase;
    margin-bottom: 10px;
    border-bottom: 1px solid #e8e9eb;
    padding-bottom: 5px;
  }

  .info-row {
    display: flex;
    padding: 6px 0;
    border-bottom: 1px solid #f5f5f5;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-size: 13px;

    .label {
      font-weight: 600;
      color: #153749;
      min-width: 140px;
    }

    .value {
      color: #153749;
      word-break: break-word;

      a {
        color: #e25d34;
        text-decoration: none;
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
`;

const WineTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${(props) => props.theme.fonts.sfcompact_text};
  font-size: 12px;

  th {
    font-size: 11px;
    text-transform: uppercase;
    color: #6c757d;
    text-align: left;
    padding: 6px 8px;
    border-bottom: 2px solid #e8e9eb;
  }

  td {
    padding: 6px 8px;
    border-bottom: 1px solid #f0f0f0;
    color: #2b3d54;
  }

  .num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
`;

const EvoBadge = styled.span<{ $evo: string }>`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  ${(props) => {
    switch (props.$evo) {
      case 'won':
        return 'background: #d4edda; color: #155724;';
      case 'lost':
        return 'background: #f8d7da; color: #721c24;';
      default:
        return 'background: #e2e3e5; color: #383d41;';
    }
  }}
`;

const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${(props) => props.theme.fonts.sfcompact_text};
  font-size: 12px;

  th {
    font-size: 11px;
    text-transform: uppercase;
    color: #6c757d;
    text-align: left;
    padding: 6px 8px;
    border-bottom: 2px solid #e8e9eb;
  }

  td {
    padding: 6px 8px;
    border-bottom: 1px solid #f0f0f0;
    color: #2b3d54;
  }

  tr:last-child td {
    background: #fafbfc;
  }

  .num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  .method-badge {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
  }

  .method-online {
    background: #e8f4fd;
    color: #0c5460;
  }

  .method-offline {
    background: #fff3cd;
    color: #856404;
  }

  .current-tag {
    display: inline-block;
    margin-left: 6px;
    padding: 0 5px;
    border-radius: 3px;
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    background: #2b3d54;
    color: white;
    vertical-align: middle;
  }

  .note-text {
    font-size: 11px;
    color: #6c757d;
    font-style: italic;
  }
`;

const ActivitySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AddActivityButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #e25d34;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 13px;
  font-family: ${(props) => props.theme.fonts.sfcompact_text};
  cursor: pointer;

  &:hover {
    background: #c94d2a;
  }
`;

const ActivityForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e8e9eb;

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
  }

  select,
  input,
  textarea {
    padding: 6px 10px;
    border: 1px solid #d0d0d0;
    border-radius: 4px;
    font-size: 13px;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};

    &:focus {
      outline: none;
      border-color: #e25d34;
    }
  }

  textarea {
    min-height: 60px;
    resize: vertical;
  }

  .form-actions {
    display: flex;
    gap: 8px;
  }

  .btn-save {
    padding: 6px 16px;
    background: #e25d34;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
  }

  .btn-cancel {
    padding: 6px 16px;
    background: white;
    color: #2b3d54;
    border: 1px solid #d0d0d0;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
  }
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const TimelineItem = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;

  .timeline-marker {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 40px;

    .dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #e25d34;
    }

    .line {
      flex: 1;
      width: 2px;
      background: #e8e9eb;
    }
  }

  .timeline-content {
    flex: 1;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};

    .activity-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 4px;
    }

    .activity-type {
      font-weight: 600;
      font-size: 13px;
      color: #2b3d54;
      text-transform: capitalize;
    }

    .activity-date {
      font-size: 12px;
      color: #6c757d;
    }

    .activity-notes {
      font-size: 13px;
      color: #444;
      line-height: 1.4;
    }

    .activity-outcome {
      display: inline-block;
      margin-top: 4px;
      padding: 1px 8px;
      border-radius: 8px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
    }

    .outcome-positive {
      background: #d4edda;
      color: #155724;
    }
    .outcome-negative {
      background: #f8d7da;
      color: #721c24;
    }
    .outcome-neutral {
      background: #e2e3e5;
      color: #383d41;
    }
    .outcome-pending {
      background: #fff3cd;
      color: #856404;
    }
  }
`;

// ─── Constants ───

const ACTIVITY_TYPES: { value: ActivityType; label: string }[] = [
  { value: 'call', label: 'Phone Call' },
  { value: 'visit', label: 'Visit' },
  { value: 'email', label: 'Email' },
  { value: 'tasting', label: 'Tasting' },
  { value: 'wine_list_upload', label: 'Add Wine List' },
  { value: 'sent_to_distributor', label: 'Sent to Distributor' },
  { value: 'other', label: 'Other' }
];

const ACTIVITY_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  ACTIVITY_TYPES.map((t) => [t.value, t.label])
);

const OUTCOME_OPTIONS: { value: ActivityOutcome; label: string }[] = [
  { value: 'positive', label: 'Positive' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'negative', label: 'Negative' },
  { value: 'pending', label: 'Pending' }
];

const CRM_STATUS_OPTIONS: { value: CrmStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'won', label: 'Won' },
  { value: 'to_win_over', label: 'To Win Over' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'lost', label: 'Lost' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'under_review', label: 'Under Review' }
];

// ─── Component ───

interface Props {
  restaurantId: number;
}

const RestaurantDetail: React.FC<Props> = ({ restaurantId }) => {
  const { getRestaurant, getCrmData, addActivity, updateCrmStatus } =
    usePerformance();
  const restaurant = getRestaurant(restaurantId);
  const crm = getCrmData(restaurantId);

  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState<ActivityType>('call');
  const [formDate, setFormDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [formNotes, setFormNotes] = useState('');
  const [formOutcome, setFormOutcome] = useState<ActivityOutcome>('pending');
  const [formFile, setFormFile] = useState<File | null>(null);

  if (!restaurant) {
    return (
      <DetailContainer>
        <BackButton onClick={() => history.push('/performance/restaurants')}>
          ← Back to restaurants
        </BackButton>
        <Card>
          <p style={{ textAlign: 'center', color: '#6c757d', padding: 40 }}>
            Restaurant not found
          </p>
        </Card>
      </DetailContainer>
    );
  }

  const activities = crm?.activities || [];

  const handleSaveActivity = () => {
    const isWineList = formType === 'wine_list_upload';
    if (isWineList && !formFile) return;

    const notes = isWineList
      ? `Wine list uploaded: ${formFile!.name}${
          formNotes.trim() ? ' — ' + formNotes.trim() : ''
        }`
      : formNotes.trim() || `${ACTIVITY_TYPE_LABELS[formType] || formType}`;

    addActivity(restaurantId, {
      type: formType,
      date: formDate,
      notes,
      outcome: isWineList ? 'pending' : formOutcome,
      attachment_name: isWineList ? formFile!.name : undefined
    });
    setShowForm(false);
    setFormNotes('');
    setFormOutcome('pending');
    setFormFile(null);
  };

  const renderBottles = (bottles: BottleDetail[] | null, title: string) => {
    if (!bottles || bottles.length === 0) return null;
    return (
      <InfoSection>
        <div className="section-title">{title}</div>
        <WineTable>
          <thead>
            <tr>
              <th>Wine</th>
              <th>Vintage</th>
              <th>Format</th>
              <th className="num">Price (GBP)</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {bottles.map((b, i) => (
              <tr key={`${b.wine_name}-${b.vintage}-${b.format_size}-${i}`}>
                <td>{b.wine_name}</td>
                <td>{b.vintage}</td>
                <td>{b.format_group}</td>
                <td className="num">
                  {b.local_price ? `£${b.local_price}` : '-'}
                </td>
                <td>
                  <EvoBadge $evo={b.evolution}>{b.evolution}</EvoBadge>
                </td>
              </tr>
            ))}
          </tbody>
        </WineTable>
      </InfoSection>
    );
  };

  const renderWineListHistory = (snapshots: WineListSnapshot[]) => {
    const sorted = [...snapshots].sort((a, b) =>
      a.collection_date.localeCompare(b.collection_date)
    );
    const lastIdx = sorted.length - 1;
    return (
      <HistoryTable>
        <thead>
          <tr>
            <th>Date</th>
            <th>Method</th>
            <th>Client Status</th>
            <th className="num">Client Refs</th>
            <th className="num">Total Refs</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((s, i) => (
            <tr key={s.collection_date}>
              <td>
                {s.collection_date}
                {i === lastIdx && <span className="current-tag">current</span>}
              </td>
              <td>
                <span className={`method-badge method-${s.collection_method}`}>
                  {s.collection_method}
                </span>
              </td>
              <td>
                <EvoBadge $evo={s.client_status}>{s.client_status}</EvoBadge>
              </td>
              <td className="num">{s.client_refs}</td>
              <td className="num">{s.total_refs}</td>
              <td className="note-text">{s.notes || ''}</td>
            </tr>
          ))}
        </tbody>
      </HistoryTable>
    );
  };

  return (
    <DetailContainer>
      <BackButton onClick={() => history.push('/performance/restaurants')}>
        ← Back to restaurants
      </BackButton>

      {/* Profile header */}
      <Card>
        <ProfileHeader>
          <div className="restaurant-info">
            <h1 className="restaurant-name">
              {restaurant.name}
              {restaurant.michelin_stars > 0 && (
                <span
                  style={{
                    marginLeft: 8,
                    display: 'inline-flex',
                    gap: 3,
                    verticalAlign: 'middle'
                  }}
                >
                  {Array.from({ length: restaurant.michelin_stars }).map(
                    (_, i) => (
                      <Icon
                        key={i}
                        icon="michelinStarIcon"
                        alt="Michelin Star"
                        style={{ color: '#D3072B', width: 20, height: 20 }}
                      />
                    )
                  )}
                </span>
              )}
            </h1>
            <div className="restaurant-meta">
              <span className="meta-item">
                Market:{' '}
                <strong style={{ marginLeft: 4 }}>{restaurant.city}</strong>
              </span>
              <span className="meta-item">
                Address:{' '}
                <strong style={{ marginLeft: 4 }}>
                  {restaurant.address}, {restaurant.postal_code}
                </strong>
              </span>
              {restaurant.hotel && (
                <span className="meta-item">
                  Hotel:{' '}
                  <strong style={{ marginLeft: 4 }}>{restaurant.hotel}</strong>
                </span>
              )}
              {restaurant.ws_award && (
                <span className="meta-item" style={{ color: '#e25d34' }}>
                  🏆 {restaurant.ws_award}
                </span>
              )}
            </div>
          </div>
          <div className="status-controls">
            <StatusSelect
              value={crm?.crm_status || 'prospect'}
              onChange={(e) =>
                updateCrmStatus(restaurantId, e.target.value as CrmStatus)
              }
            >
              {CRM_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </StatusSelect>
          </div>
        </ProfileHeader>
      </Card>

      <ContentGrid>
        {/* Left column — profile + wine lists */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Contact info */}
          <Card>
            <CardHeader title="Contact Information" />
            <InfoSection>
              <div className="info-row">
                <span className="label">Phone</span>
                <span className="value">{restaurant.phone || '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">Email</span>
                <span className="value">
                  {restaurant.email ? (
                    <a href={`mailto:${restaurant.email}`}>
                      {restaurant.email}
                    </a>
                  ) : (
                    '-'
                  )}
                </span>
              </div>
              <div className="info-row">
                <span className="label">Website</span>
                <span className="value">
                  {restaurant.site ? (
                    <a
                      href={restaurant.site}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {new URL(restaurant.site).hostname}
                    </a>
                  ) : (
                    '-'
                  )}
                </span>
              </div>
              <div className="info-row">
                <span className="label">Wine List Size</span>
                <span className="value">{restaurant.dn_total} references</span>
              </div>
              <div className="info-row">
                <span className="label">Avg. Price</span>
                <span className="value">
                  {restaurant.avg_all_price
                    ? `€${restaurant.avg_all_price}`
                    : '-'}
                </span>
              </div>
              <div className="info-row">
                <span className="label">Last Checked</span>
                <span className="value">{restaurant.checks_validity_date}</span>
              </div>
            </InfoSection>
          </Card>

          {/* My References */}
          <Card>
            <CardHeader title="My References" />
            {renderBottles(
              restaurant.client_bottles_details,
              'Current Listings'
            )}
            {(!restaurant.client_bottles_details ||
              restaurant.client_bottles_details.length === 0) && (
              <p
                style={{
                  color: '#6c757d',
                  fontSize: 13,
                  textAlign: 'center',
                  padding: 20
                }}
              >
                No current listings at this restaurant
              </p>
            )}
          </Card>

          {/* Competitor References */}
          <Card>
            <CardHeader title="Competitor References" />
            {renderBottles(
              restaurant.rival_bottles_details,
              'Competitor Wines'
            )}
            {(!restaurant.rival_bottles_details ||
              restaurant.rival_bottles_details.length === 0) && (
              <p
                style={{
                  color: '#6c757d',
                  fontSize: 13,
                  textAlign: 'center',
                  padding: 20
                }}
              >
                No competitor data available
              </p>
            )}
          </Card>

          {/* Wine List Change History */}
          {crm?.wine_list_history && crm.wine_list_history.length > 0 && (
            <Card>
              <CardHeader title="Wine List Change History" />
              {renderWineListHistory(crm.wine_list_history)}
            </Card>
          )}
        </div>

        {/* Right column — activity timeline */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Card>
            <CardHeader title="Activity" />
            <ActivitySection>
              <AddActivityButton onClick={() => setShowForm(!showForm)}>
                + Add Activity
              </AddActivityButton>

              {showForm && (
                <ActivityForm>
                  <div className="form-row">
                    <select
                      value={formType}
                      onChange={(e) =>
                        setFormType(e.target.value as ActivityType)
                      }
                    >
                      {ACTIVITY_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="date"
                      value={formDate}
                      onChange={(e) => setFormDate(e.target.value)}
                    />
                    <select
                      value={formOutcome}
                      onChange={(e) =>
                        setFormOutcome(e.target.value as ActivityOutcome)
                      }
                    >
                      {OUTCOME_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {formType === 'wine_list_upload' && (
                    <div
                      style={{
                        border: '2px dashed #d0d0d0',
                        borderRadius: 4,
                        padding: 20,
                        textAlign: 'center',
                        background: formFile ? '#f0fff0' : '#fafafa',
                        cursor: 'pointer',
                        transition: 'all 0.15s'
                      }}
                    >
                      <input
                        type="file"
                        id="wine-list-upload"
                        accept=".pdf,.xlsx,.xls,.csv,.doc,.docx,.jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          setFormFile(file);
                        }}
                      />
                      <label
                        htmlFor="wine-list-upload"
                        style={{ cursor: 'pointer', display: 'block' }}
                      >
                        {formFile ? (
                          <>
                            <span style={{ fontSize: 20 }}>📄</span>
                            <div style={{ fontWeight: 600, marginTop: 4 }}>
                              {formFile.name}
                            </div>
                            <div
                              style={{
                                fontSize: 12,
                                color: '#6c757d',
                                marginTop: 2
                              }}
                            >
                              {(formFile.size / 1024).toFixed(0)} KB — Click to
                              change
                            </div>
                          </>
                        ) : (
                          <>
                            <span style={{ fontSize: 24 }}>📋</span>
                            <div style={{ fontWeight: 600, marginTop: 4 }}>
                              Upload Wine List
                            </div>
                            <div
                              style={{
                                fontSize: 12,
                                color: '#6c757d',
                                marginTop: 2
                              }}
                            >
                              PDF, Excel, CSV, or image — click or drag to
                              upload
                            </div>
                          </>
                        )}
                      </label>
                    </div>
                  )}
                  <textarea
                    placeholder={
                      formType === 'wine_list_upload'
                        ? 'Optional notes about this wine list...'
                        : 'Notes about this activity...'
                    }
                    value={formNotes}
                    onChange={(e) => setFormNotes(e.target.value)}
                  />
                  <div className="form-actions">
                    <button className="btn-save" onClick={handleSaveActivity}>
                      {formType === 'wine_list_upload'
                        ? 'Submit Wine List'
                        : 'Save Activity'}
                    </button>
                    <button
                      className="btn-cancel"
                      onClick={() => setShowForm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </ActivityForm>
              )}

              <Timeline>
                {activities.length === 0 && (
                  <p
                    style={{
                      color: '#6c757d',
                      fontSize: 13,
                      textAlign: 'center',
                      padding: 20
                    }}
                  >
                    No activities recorded yet
                  </p>
                )}
                {activities.map((act) => (
                  <TimelineItem key={act.id}>
                    <div className="timeline-marker">
                      <div className="dot" />
                      <div className="line" />
                    </div>
                    <div className="timeline-content">
                      <div className="activity-header">
                        <span className="activity-type">
                          {ACTIVITY_TYPE_LABELS[act.type] || act.type}
                        </span>
                        <span className="activity-date">{act.date}</span>
                      </div>
                      <p className="activity-notes">{act.notes}</p>
                      {act.attachment_name && (
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            marginTop: 4,
                            padding: '2px 8px',
                            background: '#e8f4fd',
                            borderRadius: 4,
                            fontSize: 12,
                            color: '#0c5460'
                          }}
                        >
                          📄 {act.attachment_name}
                          <span style={{ color: '#6c757d', marginLeft: 4 }}>
                            — Processing
                          </span>
                        </div>
                      )}
                      <span
                        className={`activity-outcome outcome-${act.outcome}`}
                      >
                        {act.outcome}
                      </span>
                    </div>
                  </TimelineItem>
                ))}
              </Timeline>
            </ActivitySection>
          </Card>
        </div>
      </ContentGrid>
    </DetailContainer>
  );
};

export default RestaurantDetail;
